import type {
  NavDestination,
  NavGroup,
  NavGroupDestinations,
  NavModule,
  NavSection,
} from '@/components/px/nav'
import { isClientSideRole, type RoleKey } from '@/access/roles'

/**
 * The access policy — POLICY, not data, and deliberately NOT a slice. Every
 * value here is pure and derived from the role key plus the caller's INJECTED
 * context, so nothing about who-may-see-what is ever stored (spec I-7).
 *
 * This is what makes the ticket's central claim true: role decides which
 * DESTINATIONS EXIST, not merely which rows are filtered. `canAccess` drives
 * the router's refusal page and the rail filter from the same table.
 *
 * ⚠ PX-W6.6 (TASK-188E6E40, spec §6.3 item 6) removed this module's eight
 * imports from `@/demo/data`. The destination semantics (`ROLE_ACCESS`,
 * `canAccess`, `rolesWithAccess`, `landingRouteFor`, `navForRole`) are
 * BYTE-IDENTICAL; only the SCOPING helpers changed shape, taking an
 * {@link AccessContext} instead of reaching into the seed. `src/demo/scoping.ts`
 * supplies the seed-built context for the concept surfaces that still render
 * fixture rows. Access policy over a fixture was the category error — a policy
 * module must be pure over whatever entities it is handed.
 *
 * ⚠ Authentication changes nothing here (I-19). Signing in selects a lens;
 * the access set for role `r` is exactly `ROLE_ACCESS[r]`.
 */

export type RedactedFact =
  | 'rate'
  | 'cost'
  | 'margin'
  | 'utilisation'
  | 'internal-note'
  | 'agent-raw-output'

export const REDACTED_FACTS: readonly RedactedFact[] = [
  'rate',
  'cost',
  'margin',
  'utilisation',
  'internal-note',
  'agent-raw-output',
] as const

export type ProjectScope = 'all' | 'office' | 'assigned' | 'client' | 'single'

/** Only `super-admin` is `all-firms` (I-16). */
export type FirmScope = 'anchor' | 'all-firms'

export interface RoleAccess {
  role: RoleKey
  landingRoute: string
  /** Route path patterns this role may reach; ':param' and a trailing '/*' are supported. */
  allow: readonly string[]
  firmScope: FirmScope
  projectScope: ProjectScope
  redacts: readonly RedactedFact[]
}

/* ── Route groups, so an allow list reads as a decision, not a wall ─────── */

/**
 * `/` and `/designs` are reachable by EVERY role: `/` is the shared entry
 * point that renders the role's own landing, and `/designs` is linked from the
 * locked shell header — denying it would put a blocked link inside the shell.
 */
/**
 * PX-W6.2 (review C2): `/notifications` is BASE — the notification endpoints
 * are plain `@Authenticated` org-member surfaces and the bell renders in the
 * shell for every signed-in role, so every role can also reach the centre.
 */
const BASE = ['/', '/home', '/designs', '/notifications'] as const

/*
 * PX-W6.2 (TASK-A7B7080E) — additive surface groups. Messages and the client
 * register are firm-side full-group surfaces; the three admin routes below
 * ride ADMIN_ALLOW (super-admin inherits by construction). No matching-engine
 * or structure change. (The notifications centre lives in BASE above.)
 */
const MESSAGING_SURFACES = ['/messages'] as const
const CLIENT_REGISTER_SURFACES = ['/clients/register'] as const
const ORG_ADMIN_SURFACES = ['/admin/people', '/admin/settings', '/admin/audit'] as const

/*
 * PX-W6.5 (TASK-AAB07DC3) — the configuration surfaces: module catalogue,
 * outbound credentials + the OAuth SPA callback child route, and the runtime
 * page (per-project LLM config + scheduler + context index). These are
 * deployment/organisation configuration, so they ride the admin-mapped roles
 * only (the backend gates the underlying endpoints orgAdmin=true or project
 * WRITE — the client renders that projection, never re-derives it).
 */
const ADMIN_CONFIG_ROUTES = [
  '/admin/modules',
  '/admin/connectors',
  '/admin/connectors/callback',
  '/admin/runtime'
] as const

const PEOPLE_SURFACES = ['/resources', '/resources/pool', '/resources/people', '/resources/people/:personId'] as const

const RESOURCES_FULL = [
  ...PEOPLE_SURFACES,
  '/resources/capacity',
  '/resources/allocations',
  '/resources/skills',
  '/resources/utilisation',
  '/resources/pipeline',
  '/resources/training',
  '/resources/badges',
] as const

const MANAGER_RESOURCES = [
  ...PEOPLE_SURFACES,
  '/resources/capacity',
  '/resources/allocations',
  '/resources/utilisation',
] as const

const DELIVERY_SURFACES = ['/projects', '/projects/health', '/clients'] as const

const LIBRARY_READ = [
  '/library',
  '/library/templates',
  '/library/templates/:templateId',
  '/library/requirements',
  '/library/agents',
] as const

/**
 * An agent's DETAIL page is its configuration surface (`AgentConfigPage`), so
 * it sits with the other configuration routes rather than with the read-only
 * library. That is what makes the design spec's per-role rail table true: a
 * consultant reaches the agent LIBRARY and not the agent CONFIGURATION, and
 * the "Agent configuration" rail item therefore renders for exactly the two
 * roles that own it.
 */
const LIBRARY_CONFIG = [
  '/library/agents/:agentId',
  '/library/agents/:agentId/guardrails',
  '/library/blueprints',
  '/library/playbooks',
] as const

/** Every screen of one engagement. */
const PROJECT_SURFACES = [
  '/project',
  '/project/:projectId',
  '/project/:projectId/interviews',
  '/project/:projectId/requirements',
  '/project/:projectId/blueprint',
  '/project/:projectId/build',
  '/project/:projectId/migration',
  '/project/:projectId/test',
  '/project/:projectId/go-live',
  '/project/:projectId/ams',
  // PX-W6.4 (TASK-191D0FE9): the commercial project surfaces — the agreed
  // budget with its roll-up, and the AI-run spend metering.
  '/project/:projectId/budget',
  '/project/:projectId/agent-spend',
] as const

/** Where a kanban card lands, and the overview behind it. */
const BOARD_CARD_SURFACES = ['/project', '/project/:projectId', '/project/:projectId/build'] as const

/** The four roles the ticket leaves unchanged keep today's Home and today's rails. */
const UNCHANGED_ROLE_ALLOW = [
  ...BASE,
  ...RESOURCES_FULL,
  ...DELIVERY_SURFACES,
  ...LIBRARY_READ,
  ...LIBRARY_CONFIG,
  ...PROJECT_SURFACES,
  ...MESSAGING_SURFACES,
  ...CLIENT_REGISTER_SURFACES,
  // PX-W6.4: everyone who logs time gets their own timesheet.
  '/my/timesheet',
  '/portal',
] as const

const ADMIN_ALLOW = [
  ...BASE,
  ...RESOURCES_FULL,
  ...DELIVERY_SURFACES,
  ...LIBRARY_READ,
  ...LIBRARY_CONFIG,
  ...PROJECT_SURFACES,
  ...MESSAGING_SURFACES,
  ...CLIENT_REGISTER_SURFACES,
  '/my/timesheet',
  // PX-W6.4: the admin also decides submitted hours and owns the rate cards.
  '/my/approvals',
  '/admin/rates',
  '/admin/projects',
  ...ORG_ADMIN_SURFACES,
  // PX-W6.5 (TASK-AAB07DC3): the configuration surfaces — module catalogue,
  // connectors (+ the OAuth SPA callback child), runtime. Admin-mapped
  // standing only; super-admin inherits through the construction below.
  ...ADMIN_CONFIG_ROUTES,
] as const

/**
 * The super admin's allow list is CONSTRUCTED from the PX admin's, so AC-5.5
 * ("super admin is a superset of PX admin") holds structurally rather than by
 * assertion — there is no way to widen one without widening the other.
 */
const SUPER_ADMIN_ALLOW = [...ADMIN_ALLOW, '/admin/clients', '/architecture'] as const

const CLIENT_REDACTIONS = REDACTED_FACTS

export const ROLE_ACCESS: Readonly<Record<RoleKey, RoleAccess>> = {
  'project-manager': {
    role: 'project-manager',
    landingRoute: '/home',
    // PX-W6.4: the shared timesheet plus the approvals queue (spec §6.3 —
    // approvals reach manager, project-manager and admin; hr/expert/analyst
    // keep the timesheet only, which is why this is not in the shared group).
    allow: [...UNCHANGED_ROLE_ALLOW, '/my/approvals'],
    firmScope: 'anchor',
    projectScope: 'all',
    redacts: [],
  },
  consultant: {
    role: 'consultant',
    landingRoute: '/my/board',
    allow: [
      ...BASE,
      '/my/board',
      // PX-W6.4: consultants log their own hours.
      '/my/timesheet',
      ...PEOPLE_SURFACES,
      ...LIBRARY_READ,
      ...BOARD_CARD_SURFACES,
    ],
    firmScope: 'anchor',
    projectScope: 'assigned',
    redacts: [],
  },
  manager: {
    role: 'manager',
    landingRoute: '/my/team',
    allow: [
      ...BASE,
      '/my/team',
      // PX-W6.4: the timesheet and the decision queue.
      '/my/timesheet',
      '/my/approvals',
      ...MANAGER_RESOURCES,
      ...DELIVERY_SURFACES,
      ...LIBRARY_READ,
      ...PROJECT_SURFACES,
    ],
    firmScope: 'anchor',
    projectScope: 'office',
    redacts: [],
  },
  hr: {
    role: 'hr',
    landingRoute: '/home',
    allow: UNCHANGED_ROLE_ALLOW,
    firmScope: 'anchor',
    projectScope: 'all',
    // HR staffs and develops people; engagement margin is not their fact.
    redacts: ['margin'],
  },
  expert: {
    role: 'expert',
    landingRoute: '/home',
    allow: UNCHANGED_ROLE_ALLOW,
    firmScope: 'anchor',
    projectScope: 'all',
    redacts: [],
  },
  analyst: {
    role: 'analyst',
    landingRoute: '/home',
    allow: UNCHANGED_ROLE_ALLOW,
    firmScope: 'anchor',
    projectScope: 'all',
    redacts: [],
  },
  admin: {
    role: 'admin',
    landingRoute: '/admin/projects',
    allow: ADMIN_ALLOW,
    firmScope: 'anchor',
    projectScope: 'all',
    redacts: [],
  },
  'super-admin': {
    role: 'super-admin',
    landingRoute: '/admin/clients',
    allow: SUPER_ADMIN_ALLOW,
    firmScope: 'all-firms',
    projectScope: 'all',
    redacts: [],
  },
  'client-manager': {
    role: 'client-manager',
    landingRoute: '/my/client',
    allow: [...BASE, '/my/client', '/portal'],
    firmScope: 'anchor',
    projectScope: 'client',
    redacts: CLIENT_REDACTIONS,
  },
  'client-employee': {
    role: 'client-employee',
    landingRoute: '/my/project',
    allow: [...BASE, '/my/project', '/portal'],
    firmScope: 'anchor',
    projectScope: 'single',
    redacts: CLIENT_REDACTIONS,
  },
}

export const landingRouteFor = (role: RoleKey): string => ROLE_ACCESS[role].landingRoute

/* ── Route matching ─────────────────────────────────────────────────────── */

const segments = (pathname: string): string[] => {
  const clean = pathname.split('?')[0]?.split('#')[0] ?? ''
  return clean.split('/').filter(Boolean)
}

/**
 * EXACT-SEGMENT matching, never `startsWith` — `/my/board` must not grant
 * `/my/board-secret`. `:param` matches exactly one non-empty segment; a
 * trailing `/*` matches one or more.
 */
const patternMatches = (pattern: string, pathname: string): boolean => {
  const p = segments(pattern)
  const a = segments(pathname)
  const wildcard = p[p.length - 1] === '*'
  if (wildcard) {
    const head = p.slice(0, -1)
    if (a.length <= head.length) return false
    return head.every((seg, i) => seg.startsWith(':') || seg === a[i])
  }
  if (p.length !== a.length) return false
  return p.every((seg, i) => seg.startsWith(':') || seg === a[i])
}

export const canAccess = (role: RoleKey, pathname: string): boolean =>
  ROLE_ACCESS[role].allow.some((pattern) => patternMatches(pattern, pathname))

/** Powers `RoleBlockedPage` — who could have opened this, if not you. */
export const rolesWithAccess = (pathname: string): readonly RoleKey[] =>
  (Object.keys(ROLE_ACCESS) as RoleKey[]).filter((role) => canAccess(role, pathname))

/* ── Scoping ────────────────────────────────────────────────────────────── */

/**
 * The MINIMUM shape each scoping helper reads. The functions are generic over
 * the caller's own entity types, so a caller gets its own rows back — the
 * policy never widens or narrows a domain type it does not own.
 */
export interface AccessFirm {
  id: string
}

export interface AccessClient {
  id: string
  firmId: string
}

export interface AccessProject {
  id: string
  clientId: string
  /** The delivering office; compared against {@link AccessPrincipal.office}. */
  office: string
}

export interface AccessWorkPackage {
  projectId: string
  /** Whether a client-side principal may see the package at all. */
  clientVisible: boolean
}

/**
 * Who the caller IS, in scoping terms — the four facts the scoped arms need.
 * `null` means "not bound", which every arm treats as "sees nothing" rather
 * than "sees everything" (least privilege).
 */
export interface AccessPrincipal {
  /** Office code for the `office` project scope. */
  office: string | null
  /** Project ids the principal is staffed on, for the `assigned` scope. */
  assignedProjectIds: readonly string[]
  /** The client whose projects a `client`-scoped principal may see. */
  clientId: string | null
  /** The one project a `single`-scoped principal is bound to. */
  projectId: string | null
}

/**
 * Everything the scoping helpers are allowed to look at. The caller assembles
 * it from whatever it has — the live projects state, a test fixture, or (for
 * the badged concept surfaces) `src/demo/scoping.ts`'s seed-built context.
 */
export interface AccessContext<
  F extends AccessFirm = AccessFirm,
  C extends AccessClient = AccessClient,
  P extends AccessProject = AccessProject,
  W extends AccessWorkPackage = AccessWorkPackage,
> {
  /** The firm every non-`all-firms` role is confined to (I-16). */
  anchorFirmId: string
  firms: readonly F[]
  clients: readonly C[]
  projects: readonly P[]
  workPackages: readonly W[]
  principal: AccessPrincipal
}

/**
 * Firm scope is evaluated BEFORE project scope: a role that cannot see a firm
 * can never see that firm's clients or its projects (I-16).
 */
export const visibleFirms = <F extends AccessFirm>(
  role: RoleKey,
  ctx: Pick<AccessContext<F>, 'anchorFirmId' | 'firms'>
): readonly F[] =>
  ROLE_ACCESS[role].firmScope === 'all-firms'
    ? ctx.firms
    : ctx.firms.filter((firm) => firm.id === ctx.anchorFirmId)

export const visibleClients = <F extends AccessFirm, C extends AccessClient>(
  role: RoleKey,
  ctx: Pick<AccessContext<F, C>, 'anchorFirmId' | 'firms' | 'clients'>
): readonly C[] => {
  const firmIds = new Set(visibleFirms(role, ctx).map((firm) => firm.id))
  return ctx.clients.filter((client) => firmIds.has(client.firmId))
}

export const visibleProjects = <
  F extends AccessFirm,
  C extends AccessClient,
  P extends AccessProject,
>(
  role: RoleKey,
  ctx: Pick<AccessContext<F, C, P>, 'anchorFirmId' | 'firms' | 'clients' | 'projects' | 'principal'>
): readonly P[] => {
  const { principal } = ctx
  const firmClientIds = new Set(visibleClients(role, ctx).map((client) => client.id))
  const inScopeFirms = ctx.projects.filter((project) => firmClientIds.has(project.clientId))

  switch (ROLE_ACCESS[role].projectScope) {
    case 'all':
      return inScopeFirms
    case 'office': {
      if (principal.office === null) return []
      return inScopeFirms.filter((project) => project.office === principal.office)
    }
    case 'assigned': {
      const mine = new Set(principal.assignedProjectIds)
      return inScopeFirms.filter((project) => mine.has(project.id))
    }
    case 'client':
      return principal.clientId === null
        ? []
        : inScopeFirms.filter((project) => project.clientId === principal.clientId)
    case 'single':
      return principal.projectId === null
        ? []
        : inScopeFirms.filter((project) => project.id === principal.projectId)
  }
}

/**
 * Redaction is a PROJECTION, applied here before objects reach JSX — never a
 * per-component `if (role === …)` conditional (I-3).
 */
export const visibleWorkPackages = <
  F extends AccessFirm,
  C extends AccessClient,
  P extends AccessProject,
  W extends AccessWorkPackage,
>(
  role: RoleKey,
  ctx: AccessContext<F, C, P, W>,
  projectId?: string
): readonly W[] => {
  const scoped = new Set(visibleProjects(role, ctx).map((project) => project.id))
  return ctx.workPackages.filter(
    (pkg) =>
      scoped.has(pkg.projectId) &&
      (projectId === undefined || pkg.projectId === projectId) &&
      (!isClientSideRole(role) || pkg.clientVisible)
  )
}

/** How many packages a client-side role is NOT being shown — the AC-2.4 count. */
export const withheldWorkPackageCount = <
  F extends AccessFirm,
  C extends AccessClient,
  P extends AccessProject,
  W extends AccessWorkPackage,
>(
  role: RoleKey,
  ctx: AccessContext<F, C, P, W>,
  projectId?: string
): number => {
  if (!isClientSideRole(role)) return 0
  const scoped = new Set(visibleProjects(role, ctx).map((project) => project.id))
  return ctx.workPackages.filter(
    (pkg) =>
      scoped.has(pkg.projectId) &&
      (projectId === undefined || pkg.projectId === projectId) &&
      !pkg.clientVisible
  ).length
}

export const redacts = (role: RoleKey, fact: RedactedFact): boolean =>
  ROLE_ACCESS[role].redacts.includes(fact)

/** The firm a client belongs to — used by the cross-firm containment test. */
export const firmOfClient = <F extends AccessFirm, C extends AccessClient>(
  clientId: string,
  ctx: Pick<AccessContext<F, C>, 'anchorFirmId' | 'firms' | 'clients'>
): F | null => {
  const firmId = ctx.clients.find((client) => client.id === clientId)?.firmId ?? ctx.anchorFirmId
  return ctx.firms.find((firm) => firm.id === firmId) ?? null
}

/* ── Nav filtering ──────────────────────────────────────────────────────── */

/**
 * The SINGLE filter point (called once, centrally, inside `AppShell`). A rail
 * item survives iff the role may reach it — an item declaring NO `to` (deck
 * 0.3 "Roles & permissions") has nothing to gate on and rides the section it
 * belongs to (TASK-33343000, spec §6.4) — and a section is dropped when it is
 * empty, OR when every surviving item is destination-less: a lone Coming soon
 * row with nothing around it would be furniture for a role that cannot reach
 * the module at all (I-5).
 */
export const navForRole = (
  role: RoleKey,
  sections: readonly NavSection[]
): readonly NavSection[] =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.to === undefined || canAccess(role, item.to)),
    }))
    .filter((section) => section.items.some((item) => item.to !== undefined))

/**
 * A module resolved for one role: its filtered rail plus its openable
 * destination, or `null` when the role cannot reach it at all (TASK-33343000,
 * spec §6.4, D5). The destination is the FIRST `link`-stated item the role can
 * reach — never a fixed per-module route, since a module's own landing can be
 * denied to a role (`/my/board` is consultant-only) while a later row in the
 * same module is not.
 */
export const destinationFor = (role: RoleKey, module: NavModule): NavDestination | null => {
  const sections = navForRole(role, module.sections)
  if (sections.length === 0) return null
  const entry = sections
    .flatMap((section) => section.items)
    .find((item) => item.state === 'link' && item.to !== undefined)
  if (entry === undefined || entry.to === undefined) return null
  return {
    key: module.key,
    labelKey: module.labelKey,
    label: module.label,
    icon: module.icon,
    to: entry.to,
    sections,
  }
}

/**
 * The level-1 model for one role: every group that still has at least one
 * reachable destination, in deck order (I-6). A group every one of whose
 * modules resolves to `null` is dropped entirely, never rendered with an
 * empty heading (AC-1 scenario 3).
 */
export const navGroupsForRole = (
  role: RoleKey,
  groups: readonly NavGroup[]
): readonly NavGroupDestinations[] =>
  groups
    .map((group) => ({
      key: group.key,
      headingKey: group.headingKey,
      heading: group.heading,
      destinations: group.modules
        .map((module) => destinationFor(role, module))
        .filter((destination): destination is NavDestination => destination !== null),
    }))
    .filter((group) => group.destinations.length > 0)
